import React, { useEffect, useRef, useState } from "react";
import type { Property } from "csstype";

export interface ModalProps {
  isOpen: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  width?: Property.Width;
  /** When true, close gestures call onClose but don't close the dialog — the
   * parent must flip isOpen (lets it veto/defer closing, e.g. to confirm
   * unsaved changes). */
  guardClose?: boolean;
  /** When true, the dialog appears without its opening animation (it still
   * animates when closing), e.g. when it replaces another dialog in place. */
  skipOpenAnimation?: boolean;
}

// https://blog.logrocket.com/creating-reusable-pop-up-modal-react/
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  width,
  guardClose,
  skipOpenAnimation
}) => {
  const [isModalOpen, setModalOpen] = useState(isOpen);
  const modalRef = useRef<HTMLDialogElement | null>(null);

  const requestClose = () => {
    onClose?.();
    if (!guardClose) setModalOpen(false);
  };

  const handleClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    if (event.target === modalRef.current) {
      requestClose();
    }
  };

  // Native <dialog> closes on Escape and fires `cancel`; block that so the
  // close always flows through requestClose (and can be vetoed when guarded).
  const handleCancel = (event: React.SyntheticEvent<HTMLDialogElement>) => {
    event.preventDefault();
    requestClose();
  };

  useEffect(() => {
    setModalOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    const modalElement = modalRef.current;

    if (modalElement) {
      if (isModalOpen) {
        if (skipOpenAnimation) {
          // Suppress transitions just for the opening style change: flush
          // styles while the class is set, then drop it again so closing still
          // animates.
          modalElement.classList.add("modal--instant");
          modalElement.showModal();
          modalElement.getBoundingClientRect();
          modalElement.classList.remove("modal--instant");
        } else {
          modalElement.showModal();
        }
      } else {
        modalElement.close();
      }
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps (skipOpenAnimation only matters when opening)
  }, [isModalOpen]);

  return (
    <dialog
      ref={modalRef}
      onCancel={handleCancel}
      onClick={handleClick}
      className="modal"
      style={{ width }}
    >
      <button className="modal-close-btn" onClick={requestClose}>
        Close
      </button>
      {children}
    </dialog>
  );
};

export default Modal;

export const ModalImage: React.FC<{ children: React.ReactNode }> = props => {
  const [isOpen, setOpen] = useState(false);
  return (
    <>
      <div onClick={() => setOpen(true)} style={{ cursor: "zoom-in" }}>
        {props.children}
      </div>
      <Modal isOpen={isOpen} onClose={() => setOpen(false)} width="90vw">
        {props.children}
      </Modal>
    </>
  );
};
